import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaSearch, 
  FaCircle, 
  FaClock, 
  FaTrash, 
  FaFilter, 
  FaEllipsisV,
  FaThumbtack
} from 'react-icons/fa';
import { Search, AlertCircle } from 'lucide-react';
import { authAPI } from '../../services/api';

import expert1 from '../../assets/team.png';

export default function MessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOption, setFilterOption] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Check for expert_id in query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const expertId = searchParams.get('expert_id');
    
    if (expertId) {
      // Check if conversation already exists
      const existingConversation = conversations.find(conv => conv.expertId === parseInt(expertId));
      
      if (existingConversation) {
        // Navigate to existing conversation
        navigate(`/farmer-dashboard/messages/chat/${expertId}`);
      } else {
        // Start new conversation by navigating to chat
        navigate(`/farmer-dashboard/messages/chat/${expertId}`);
      }
    }
  }, [location.search, conversations, navigate]);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type, message: '' });
    }, 3000);
  };

  const isExpertDashboard = location.pathname.startsWith('/expert-dashboard');

  // Fetch conversations from API
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const result = await authAPI.getConversations();

        if (result.success) {
          let transformedConversations;

          transformedConversations = result.data.map(conv => {
            let lastMessage = 'No messages yet';
            let timestamp = '';

            if (conv.user.last_message && typeof conv.user.last_message === 'object') {
              lastMessage = conv.user.last_message.content || 'No messages yet';
              timestamp = conv.user.last_message.sent_at || '';
            } else if (conv.user.last_message) {
              lastMessage = conv.user.last_message;
            }

            return {
              id: conv.conversation_id,
              expertId: conv.user.id,
              expertName: conv.user.name,
              image: conv.user.avatar || expert1,
              lastMessage,
              timestamp,
              date: 'Today',
              unread: conv.user.unread_messages || 0,
              online: true,
              pinned: false
            };
          });

          setConversations(transformedConversations);
          setFilteredConversations(sortConversationsByPinned(transformedConversations));
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        showToast('error', 'Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, []);

  useEffect(() => {
    // Filter conversations based on search term and filter option
    const filtered = conversations.filter(conversation => {
      const matchesSearch = conversation.expertName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           conversation.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterOption === 'all') return matchesSearch;
      if (filterOption === 'unread') return matchesSearch && conversation.unread > 0;
      if (filterOption === 'pinned') return matchesSearch && conversation.pinned;
      
      return matchesSearch;
    });
    
    setFilteredConversations(sortConversationsByPinned(filtered));
  }, [searchTerm, filterOption, conversations]);

  // Sort conversations with pinned ones first
  const sortConversationsByPinned = (convs) => {
    return [...convs].sort((a, b) => {
      if (a.pinned === b.pinned) return 0;
      return a.pinned ? -1 : 1;
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  const handleFilter = (option) => {
    setFilterOption(option);
    setShowFilterMenu(false);
  };

  const togglePinConversation = (id, e) => {
    e.stopPropagation();
    const updatedConversations = conversations.map(conv => 
      conv.id === id ? { ...conv, pinned: !conv.pinned } : conv
    );
    setConversations(updatedConversations);
  };

  const handleDeleteClick = (conversation, e) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const confirmDeleteConversation = async () => {
    if (!conversationToDelete) return;
    
    try {
      setDeleting(true);
      const result = await authAPI.deleteConversation(conversationToDelete.id);
      
      if (result.success) {
        showToast('success', 'Conversation deleted successfully');
        const updatedConversations = conversations.filter(conv => conv.id !== conversationToDelete.id);
        setConversations(updatedConversations);
        setShowDeleteModal(false);
        setConversationToDelete(null);
      } else {
        showToast('error', result.message || 'Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      showToast('error', 'Failed to delete conversation');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setConversationToDelete(null);
  };

  const handleChatClick = (userId, e) => {
    e.preventDefault();
    if (isExpertDashboard) {
      navigate(`/expert-dashboard/messages/chat/${userId}`);
    } else {
      navigate(`/farmer-dashboard/messages/chat/${userId}`);
    }
  };

  if (loading) {
    return (
      <div className="messages-page-container_apw">
        <div className="loading">Loading conversations...</div>
      </div>
    );
  }

  return (
    <div className="messages-page-container_apw">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay-fxk">
          <div className="modal-content-fxk">
            <div className="modal-header-fxk">
              <h3>Delete Conversation</h3>
            </div>
            <div className="modal-body-fxk">
              <div className="delete-warning-fxk">
                <AlertCircle size={48} className="warning-icon" />
                <p>Are you sure you want to delete this conversation with <strong>{conversationToDelete?.expertName}</strong>?</p>
                <p className="warning-text-fxk">This action cannot be undone.</p>
              </div>
            </div>
            <div className="modal-footer-fxk">
              <button 
                className="cancel-button-fxk" 
                onClick={cancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="delete-button-fxk" 
                onClick={confirmDeleteConversation}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className="loading"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="messages-header_apw">
        <h1 className="messages-title_apw">Messages</h1>
        <div className="messages-actions_apw">
          <div className="search-wrapper-app">
            <div className="search-icon-app">
              <Search size={18} />
            </div>
            <input
              type="text"
              className="search-input-app"
              placeholder="Search through your messages "
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          
          <div className="filter-container_apw">
          {/*   <button onClick={toggleFilterMenu} className="filter-button_apw">
              <FaFilter className="filter-icon_apw" />
              <span>Filter</span>
            </button> */}
            
            {showFilterMenu && (
              <div className="filter-menu_apw">
                <button 
                  className={`filter-option_apw ${filterOption === 'all' ? 'active_apw' : ''}`} 
                  onClick={() => handleFilter('all')}
                >
                  All Conversations
                </button>
                <button 
                  className={`filter-option_apw ${filterOption === 'unread' ? 'active_apw' : ''}`} 
                  onClick={() => handleFilter('unread')}
                >
                  Unread Messages
                </button>
                <button 
                  className={`filter-option_apw ${filterOption === 'pinned' ? 'active_apw' : ''}`} 
                  onClick={() => handleFilter('pinned')}
                >
                  Pinned Chats
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="conversations-list_apw">
        {filteredConversations.length === 0 ? (
          <div className="no-conversations_apw">
            <p>No conversations found</p>
          </div>
        ) : (
          filteredConversations.map(conversation => (
            <div 
              key={conversation.id}
              className={`conversation-item_apw ${conversation.pinned ? 'pinned_apw' : ''}`}
              onClick={(e) => handleChatClick(conversation.expertId, e)}
              style={{ cursor: 'pointer' }}
            >
              <div className="conversation-avatar_apw">
                <img src={conversation.image} alt={conversation.expertName} />
                {conversation.online && <span className="online-indicator_apw"><FaCircle /></span>}
              </div>
              
              <div className="conversation-content_apw">
                <div className="conversation-header_apw">
                  <h3 className="expert-name_apw">
                    {conversation.pinned && <FaThumbtack className="pin-icon_apw" />}
                    {conversation.expertName}
                  </h3>
                  <div className="conversation-time_apw">
                    <FaClock className="time-icon_apw" />
                    <span>{conversation.timestamp}</span>
                    <span className="date-label_apw">{conversation.date}</span>
                  </div>
                </div>
                
                <div className="conversation-message_apw">
                  <p className="message-preview_apw">{conversation.lastMessage}</p>
                  {conversation.unread > 0 && (
                    <span className="unread-badge_apw">{conversation.unread}</span>
                  )}
                </div>
              </div>
              
              <div className="conversation-actions_apw" onClick={(e) => e.stopPropagation()}>
                <div className="dropdown_apw">
                  <button className="dropdown-toggle_apw">
                    <FaEllipsisV />
                  </button>
                  <div className="dropdown-menu_apw">
                    <button onClick={(e) => togglePinConversation(conversation.id, e)}>
                      {conversation.pinned ? (
                        <>
                          <FaThumbtack /> Unpin
                        </>
                      ) : (
                        <>
                          <FaThumbtack /> Pin
                        </>
                      )}
                    </button>
                    <button onClick={(e) => handleDeleteClick(conversation, e)}>
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          font-size: 16px;
          color: #6b7280;
        }

        /* Delete Confirmation Modal */
        .modal-overlay-fxk {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10000;
        }

        .modal-content-fxk {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          max-width: 400px;
          width: 90%;
          animation: modalSlideIn 0.3s ease-out;
        }

        .modal-header-fxk {
          padding: 20px 20px 0 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header-fxk h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }

        .modal-body-fxk {
          padding: 20px;
        }

        .delete-warning-fxk {
          text-align: center;
        }

        .warning-icon {
          color: #ef4444;
          margin-bottom: 16px;
        }

        .delete-warning-fxk p {
          margin: 0 0 12px 0;
          color: #374151;
          line-height: 1.5;
        }

        .warning-text-fxk {
          color: #6b7280;
          font-size: 14px;
        }

        .modal-footer-fxk {
          padding: 15px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .cancel-button-fxk {
          padding: 10px 20px;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .cancel-button-fxk:hover {
          background: #f9fafb;
        }

        .delete-button-fxk {
          padding: 10px 20px;
          border: none;
          background: #ef4444;
          color: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .delete-button-fxk:hover:not(:disabled) {
          background: #dc2626;
        }

        .delete-button-fxk:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @keyframes modalSlideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}